/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  View,
  ScrollView,
  Platform,
  Modal,
  NativeModules,
  useWindowDimensions,
} from 'react-native';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import * as Linking from 'expo-linking';
import { format, differenceInCalendarDays } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useAppConfig, useDeviceInfo } from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { useLogger } from '@airasia-rn/core-logger';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet } from 'react-native-unistyles';
import { HeaderView } from '../HeaderWidget';
import { useTranslation } from 'react-i18next';
import { FeatureDestinationView } from '../FeatureDestinationView';
import { VoucherCarouselWidget } from '../VoucherCarouselWidget';
import { HotelBannerView } from '../HotelBannerView';
import { HotelDealsCarouselView } from '../HotelDealsCarouselView';
import { RecentSearchView } from '../RecentSearchView';
import SnackbarView, {
  SnackbarDuration,
} from '@airasia-phoenix/react-native/components/SnackbarView';
import {
  useHotelConfig,
  HotelConfigContext,
} from '../../configs/HotelConfigProvider';
import { useContext } from 'react';
import {
  useRecentSearches,
  RECENT_SEARCHES_QUERY_KEY,
  flattenHotelRecentSearchesForHistory,
} from '../../hooks/useRecentSearch';
import { useSmartFillApi } from '../../hooks/hotels';
import { useHandleHotelSearch } from '../../hooks/useHandleHotelSearch';
import {
  useOptionalHotelsNavigation,
  useOptionalHotelsRoutes,
  useHotelsSearchParams,
} from '../../context/HotelsNavigationContext';
import { clearStoredSearchParams } from '../../hooks/useHotelSearchParams';
import {
  consumeMapEditReturnUrl,
  clearMapViewNavigationOrigin,
} from '../../utils/hotelSearchStorage';
import { SearchFormView } from '../SearchFormView';
import {
  buildHotelDetailsUrl,
  formatOccupancy,
  getCurrentWindowURLParams,
  getHotelSearchWidgetInitialValues,
  hasHotelSearchCriteria,
  mapRecentSearchToSuggestions,
  parseHotelDeepLinkQueryParams,
  parseHotelSearchParams,
} from '../../utils';
import { safeAreaEdges } from '../../utils/safeArea.utils';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';
import type { SearchFormPrefillData } from '../SearchFormView';
import type { HotelRecentSearch, HotelSearchData } from '../../types';
import { useIsClientHydrated, useSearchViewFeatureFlag } from '../../hooks';
import type { HotelSearchViewProps } from './types';
import { HOTEL } from '../../configs/constants';
import ArrowBackIos from '@move-travel/react-native-phoenix-icons/ArrowBackIos';

type SearchCriteriaQueryParams = Record<string, string | string[] | undefined>;
type DetailOriginParams = {
  hotelId: string;
  path?: string;
  destinationName: string;
  destinationFullName?: string;
};

const DETAIL_ORIGIN_PARAM_KEYS = new Set([
  'from_details',
  'details_hotel_id',
  'details_path',
  'details_destination_name',
  'details_destination_full_name',
  'destination_name',
]);
const SELECTED_HOTEL_PARAM_KEYS = new Set([
  'aaId',
  'aa_id',
  'user_selected_hotel_id',
  'user_selected_id',
]);
const SEARCH_FORM_CRITERIA_KEYS = [
  'locationId',
  'location_id',
  'destination',
  'destination_name',
  'destinationName',
  'checkin',
  'fromDate',
  'checkout',
  'toDate',
  'adults',
  'rooms',
  'children',
  'occupancy',
  'aaId',
  'aa_id',
  'user_selected_hotel_id',
  'user_selected_id',
  'selected_hotel_id',
  'selectedHotelId',
] as const;

const hasSearchFormCriteria = (
  params: SearchCriteriaQueryParams | null | undefined,
): boolean => {
  if (!params) {
    return false;
  }

  return SEARCH_FORM_CRITERIA_KEYS.some((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      return value.some(
        (item) => typeof item === 'string' && item.trim().length > 0,
      );
    }

    return typeof value === 'string' && value.trim().length > 0;
  });
};
const HOTEL_SEARCH_PARAMS_STORAGE_KEY = 'hotel_search_params';

type SearchParamsStorage = {
  getItem: (key: string) => string | null;
};

const getSearchParamsStorage = (): SearchParamsStorage | null => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch {
    // ignore
  }

  return (
    (globalThis as { sessionStorage?: SearchParamsStorage }).sessionStorage ??
    null
  );
};

const getStoredSearchParams = (): Record<string, string> | null => {
  const storage = getSearchParamsStorage();

  if (!storage) {
    return null;
  }

  try {
    const stored = storage.getItem(HOTEL_SEARCH_PARAMS_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Record<string, string>) : null;
  } catch {
    return null;
  }
};

const getStringParamValue = (value: string | string[] | undefined): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === 'string') || '';
  }

  return '';
};

const getDetailOriginParams = (
  params: SearchCriteriaQueryParams,
): DetailOriginParams | null => {
  if (getStringParamValue(params.from_details) !== '1') {
    return null;
  }

  return {
    hotelId: getStringParamValue(params.details_hotel_id),
    path: getStringParamValue(params.details_path) || undefined,
    destinationName: getStringParamValue(params.details_destination_name),
    destinationFullName:
      getStringParamValue(params.details_destination_full_name) || undefined,
  };
};

const sanitizeDetailOriginParams = (
  params: SearchCriteriaQueryParams,
): SearchCriteriaQueryParams =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !DETAIL_ORIGIN_PARAM_KEYS.has(key),
    ),
  ) as SearchCriteriaQueryParams;

const omitSelectedHotelParams = (
  params: SearchCriteriaQueryParams,
): SearchCriteriaQueryParams =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !SELECTED_HOTEL_PARAM_KEYS.has(key),
    ),
  ) as SearchCriteriaQueryParams;

const MAP_NAV_SKIP_PARAM_KEYS = new Set([
  ...DETAIL_ORIGIN_PARAM_KEYS,
  'from_mapview',
  'v',
  'countryCode',
  'parsedRooms',
  'hotelData',
]);

/**
 * Build a clean string-only param bag for map navigation. Drops empty values,
 * detail-origin markers, and non-serializable leftovers from session storage
 * that previously crashed the map page on mweb header-back.
 */
const sanitizeMapNavigationParams = (
  params: SearchCriteriaQueryParams,
): Record<string, string> => {
  const sanitized: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (MAP_NAV_SKIP_PARAM_KEYS.has(key)) {
      return;
    }

    if (Array.isArray(value)) {
      const items = value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0 && item !== '[object Object]');
      if (items.length > 0) {
        sanitized[key] = items.join(',');
      }
      return;
    }

    const stringValue = getStringParamValue(value).trim();
    if (!stringValue || stringValue === '[object Object]') {
      return;
    }

    sanitized[key] = stringValue;
  });

  return sanitized;
};

const LOCATION_SEED_PARAM_KEYS = [
  'location_id',
  'locationId',
  'destination',
  'destination_name',
  'destinationName',
  'checkin',
  'checkout',
  'fromDate',
  'toDate',
  'occupancy',
  'adults',
  'rooms',
  'children',
] as const;

/**
 * Overlay URL/ambient params onto stored map-return criteria without letting
 * empty overlay values wipe location / destination labels from sessionStorage.
 */
const mergeSearchCriteriaParams = (
  base: SearchCriteriaQueryParams,
  overlay: SearchCriteriaQueryParams,
): SearchCriteriaQueryParams => {
  const merged: SearchCriteriaQueryParams = { ...base, ...overlay };

  LOCATION_SEED_PARAM_KEYS.forEach((key) => {
    const overlayValue = getStringParamValue(overlay[key]);
    const baseValue = getStringParamValue(base[key]);
    if (!overlayValue && baseValue) {
      merged[key] = base[key];
    }
  });

  return merged;
};

const hasLocationSeedCriteria = (
  params: SearchCriteriaQueryParams | null | undefined,
): boolean => {
  if (!params) {
    return false;
  }

  return Boolean(
    getStringParamValue(params.location_id) ||
      getStringParamValue(params.locationId) ||
      getStringParamValue(params.destination),
  );
};

const getRouter = () => require('expo-router').router;

const HotelSearchView = ({
  onBackPress,
  onNavigate,
}: HotelSearchViewProps = {}) => {
  const navigation = useOptionalHotelsNavigation();
  const routes = useOptionalHotelsRoutes();
  const { t } = useTranslation('hotels');
  // Hydration-safe (< 768, same cut-off as breakpoint < md): follows the
  // server's device hint until mounted. getCurrentBreakpoint() is `md` on the
  // server, so the mobile header and featured destinations appeared only after
  // hydration and the form jumped down.
  const { isMobileWidth: isMobile } = useDeviceInfo();
  const { logEvent } = useLogger();
  const { EVENT_NAMES } = SearchWidgetMeta;
  const {
    hotel_recommendation_enabled,
    hotel_recent_searches_enabled,
    enable_hotel_feature_destination,
    hotel_search_voucher_enabled,
    enable_hotel_search_banner,
  } = useSearchViewFeatureFlag();

  const { geoId: countryCodeFromContext, backHandler } = useSuperAppContext();
  const { config: hotelConfig } = useHotelConfig();
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { handleSearch: handleHotelSearch } = useHandleHotelSearch();
  const queryClient = useQueryClient();
  const { width: screenWidth } = useWindowDimensions();
  const safeAreaInsets = useSafeAreaInsets();
  const routeParams = useHotelsSearchParams();
  const url = Linking.useLinkingURL();
  const latestSearchCriteriaParamsRef =
    useRef<SearchCriteriaQueryParams | null>(null);
  const latestDetailOriginParamsRef = useRef<DetailOriginParams | null>(null);
  // Defer reading browser-only URL/sessionStorage until after hydration so the
  // first client render matches SSR HTML.
  const isClientHydrated = useIsClientHydrated();
  const deeplinkUrl = hotelConfig?.deeplink ?? (isClientHydrated ? url : null);

  // On web, expo-router does not reliably populate URL query params into
  // useLocalSearchParams() for the root route after window.location.assign.
  // Read window.location.search directly as a fallback so from_mapview /
  // destination_name written by the map edit button are always readable.
  const currentWindowUrlParams = useMemo<SearchCriteriaQueryParams>(
    () =>
      isClientHydrated
        ? (getCurrentWindowURLParams() as SearchCriteriaQueryParams)
        : {},
    [isClientHydrated],
  );
  const parsedDeepLinkUrlParams = useMemo<SearchCriteriaQueryParams>(
    () => parseHotelDeepLinkQueryParams(deeplinkUrl),
    [deeplinkUrl],
  );
  const currentDetailOriginParams =
    getDetailOriginParams(routeParams as SearchCriteriaQueryParams) ||
    getDetailOriginParams(currentWindowUrlParams);
  const deeplinkParams = hasSearchFormCriteria(routeParams)
    ? routeParams
    : hasSearchFormCriteria(currentWindowUrlParams)
      ? currentWindowUrlParams
      : hasSearchFormCriteria(parsedDeepLinkUrlParams)
        ? parsedDeepLinkUrlParams
        : hasHotelSearchCriteria(routeParams)
          ? routeParams
          : hasHotelSearchCriteria(currentWindowUrlParams)
            ? currentWindowUrlParams
            : parsedDeepLinkUrlParams;
  const hasCurrentSearchFormCriteria = hasSearchFormCriteria(deeplinkParams);
  const hasCurrentDeepLinkCriteria = hasHotelSearchCriteria(deeplinkParams);
  // Live URL only — never trust a leftover from_mapview in sessionStorage.
  const isFromMapViewNavigation =
    getStringParamValue(routeParams.from_mapview) === '1' ||
    getStringParamValue(currentWindowUrlParams.from_mapview) === '1';

  // Do not lock ambient URL params (e.g. languageId/checkin-only) over
  // map-return criteria — that cleared the destination on mweb map → search.
  if (hasCurrentSearchFormCriteria && !isFromMapViewNavigation) {
    latestSearchCriteriaParamsRef.current =
      deeplinkParams as SearchCriteriaQueryParams;
  }

  if (currentDetailOriginParams) {
    latestDetailOriginParamsRef.current = currentDetailOriginParams;
  }

  const resolvedSearchCriteriaParams =
    useMemo<SearchCriteriaQueryParams>(() => {
      // Prefer live URL criteria (map edit now puts location/destination/dates
      // in the query). Merge sessionStorage only as a backup for missing keys.
      if (isClientHydrated && isFromMapViewNavigation) {
        const stored = getStoredSearchParams();
        if (stored && hasHotelSearchCriteria(stored)) {
          const merged = mergeSearchCriteriaParams(
            stored as SearchCriteriaQueryParams,
            deeplinkParams as SearchCriteriaQueryParams,
          );
          latestSearchCriteriaParamsRef.current = merged;
          return merged;
        }
      }

      if (
        latestSearchCriteriaParamsRef.current &&
        hasSearchFormCriteria(latestSearchCriteriaParamsRef.current)
      ) {
        return latestSearchCriteriaParamsRef.current;
      }
      if (isClientHydrated) {
        const stored = getStoredSearchParams();
        if (stored && hasHotelSearchCriteria(stored)) {
          latestSearchCriteriaParamsRef.current = mergeSearchCriteriaParams(
            stored as SearchCriteriaQueryParams,
            deeplinkParams as SearchCriteriaQueryParams,
          );
          return latestSearchCriteriaParamsRef.current;
        }
      }
      if (latestSearchCriteriaParamsRef.current) {
        return latestSearchCriteriaParamsRef.current;
      }
      if (hasCurrentDeepLinkCriteria) {
        return deeplinkParams as SearchCriteriaQueryParams;
      }
      return parsedDeepLinkUrlParams;
    }, [
      deeplinkParams,
      hasCurrentDeepLinkCriteria,
      isClientHydrated,
      isFromMapViewNavigation,
      parsedDeepLinkUrlParams,
    ]);
  const detailOriginParams = latestDetailOriginParamsRef.current;
  const sanitizedSearchCriteriaParams = React.useMemo(
    () => sanitizeDetailOriginParams(resolvedSearchCriteriaParams),
    [resolvedSearchCriteriaParams],
  );
  const parsedSearchCriteriaParams = React.useMemo(
    () => parseHotelSearchParams(resolvedSearchCriteriaParams),
    [resolvedSearchCriteriaParams],
  );

  const { recentSearches: rawRecentSearches, addRecentSearch } =
    useRecentSearches();
  const recentSearches = isClientHydrated ? rawRecentSearches : [];

  const deeplinkLocationId =
    getStringParamValue(resolvedSearchCriteriaParams.location_id) ||
    getStringParamValue(resolvedSearchCriteriaParams.locationId) ||
    getStringParamValue(resolvedSearchCriteriaParams.destination) ||
    '';
  // Require a location id to seed the destination field. Ambient form keys
  // (dates/occupancy alone) must not call getHotelSearchWidgetInitialValues
  // with empty location — that returns destination: null and clears the field.
  const hasDeepLinkCriteria = hasLocationSeedCriteria(
    resolvedSearchCriteriaParams,
  );
  const {
    data: deepLinkDestinationName,
    isFetching: isFetchingDestinationName,
  } = useSmartFillApi(hasDeepLinkCriteria ? deeplinkLocationId : '');

  // On native, SuperAppContext provides the real geoId from the super app.
  // On web, SuperAppContext has a hardcoded default ('MY') since no provider
  // is set up — skip  it so missing URL param correctly falls back to default banners.
  const countryCode =
    hotelConfig?.geoId ??
    appConfig.geoId ??
    (Platform.OS !== 'web' ? countryCodeFromContext : undefined);
  const [toastMessage, setToast] = useState('');

  // Trigger search widget view analytics on component mount
  useEffect(() => {
    const params = SearchWidgetEvent.searchWidgetViewEvent();
    logEvent(EVENT_NAMES.SEARCH_WIDGET_VIEWED, params);
  }, [logEvent, EVENT_NAMES.SEARCH_WIDGET_VIEWED]);

  const lastSearchedDestination = recentSearches[0];
  const destinationNameFromParams = getStringParamValue(
    resolvedSearchCriteriaParams.destination_name,
  );
  const selectedHotelIdFromParams = (
    parsedSearchCriteriaParams.aaId ||
    parsedSearchCriteriaParams.user_selected_hotel_id ||
    ''
  ).trim();
  const matchingRecentSearchDestinationName =
    deeplinkLocationId &&
    lastSearchedDestination?.destination?.id === deeplinkLocationId &&
    lastSearchedDestination?.destination?.type?.toLowerCase() !== HOTEL &&
    !lastSearchedDestination?.destination?.aa_id?.trim()
      ? lastSearchedDestination.destination.name
      : undefined;
  // Smart fill resolves the LOCATION id, so it can only ever return the
  // surrounding area — never the hotel that was searched. Letting it label a
  // destination that carries a hotel id is what made the field alternate
  // between the hotel name and the hotel's location. With a hotel in the
  // params, an explicit destination_name is the only trustworthy label.
  const resolvedDestinationName = selectedHotelIdFromParams
    ? destinationNameFromParams
    : destinationNameFromParams ||
      deepLinkDestinationName?.name ||
      matchingRecentSearchDestinationName;
  // Seeding the form from a half-resolved lookup paints one label and swaps it
  // a tick later, so hold off until the name query settles.
  const isWaitingForDestinationName =
    hasDeepLinkCriteria &&
    Boolean(deeplinkLocationId) &&
    !selectedHotelIdFromParams &&
    !destinationNameFromParams &&
    !matchingRecentSearchDestinationName &&
    isFetchingDestinationName;

  // Only pass initialFormValues when there's a deep link or recent search to apply
  // Otherwise pass undefined to let the hook use persisted cache
  const shouldApplyInitialValues =
    (hasDeepLinkCriteria || recentSearches.length > 0) &&
    !isWaitingForDestinationName;
  const initialFormValues = React.useMemo(
    () =>
      shouldApplyInitialValues
        ? getHotelSearchWidgetInitialValues({
            queryParams: hasDeepLinkCriteria
              ? resolvedSearchCriteriaParams
              : {},
            recentSearch: hasDeepLinkCriteria
              ? undefined
              : lastSearchedDestination,
            destinationName: resolvedDestinationName,
          })
        : undefined,
    [
      hasDeepLinkCriteria,
      lastSearchedDestination,
      resolvedDestinationName,
      resolvedSearchCriteriaParams,
      shouldApplyInitialValues,
    ],
  );

  const detailSearchPrefillData = React.useMemo<
    SearchFormPrefillData | undefined
  >(() => {
    if (
      !detailOriginParams?.hotelId ||
      !detailOriginParams.destinationName ||
      !parsedSearchCriteriaParams.location_id
    ) {
      return undefined;
    }

    return {
      destination: {
        aa_id: detailOriginParams.hotelId,
        city_id: null,
        coordinates: { lat: 0, lon: 0 },
        country_code:
          parsedSearchCriteriaParams.countryCode ||
          parsedSearchCriteriaParams.country_code ||
          '',
        full_name:
          detailOriginParams.destinationFullName ||
          detailOriginParams.destinationName,
        id: parsedSearchCriteriaParams.location_id,
        name: detailOriginParams.destinationName,
        type: 'hotel',
      },
    };
  }, [
    detailOriginParams,
    parsedSearchCriteriaParams.countryCode,
    parsedSearchCriteriaParams.country_code,
    parsedSearchCriteriaParams.location_id,
  ]);

  const handleBackToHome = () => {
    if (onBackPress) {
      onBackPress();
      return;
    }

    const storedSearchCriteriaParams = getStoredSearchParams();
    const hasStoredSearchCriteria =
      storedSearchCriteriaParams &&
      hasHotelSearchCriteria(storedSearchCriteriaParams);
    const backSearchCriteriaParams: SearchCriteriaQueryParams = {
      ...(hasStoredSearchCriteria
        ? (storedSearchCriteriaParams as SearchCriteriaQueryParams)
        : {}),
      ...resolvedSearchCriteriaParams,
    };
    const isFromListing =
      getStringParamValue(routeParams.from_listing) === '1' ||
      getStringParamValue(currentWindowUrlParams.from_listing) === '1' ||
      getStringParamValue(backSearchCriteriaParams.from_listing) === '1';
    // Live URL only — leftover sessionStorage from_mapview must not reopen map.
    const isFromMapView =
      getStringParamValue(routeParams.from_mapview) === '1' ||
      getStringParamValue(currentWindowUrlParams.from_mapview) === '1';

    if (isFromListing) {
      const listingParams = {
        ...backSearchCriteriaParams,
        from_listing: '1',
      };

      // Native list edit pushes search onto the listing. Header back must pop
      // (or replace if there is no history) — pushing another list recreates
      // [list, search, list], the same stack bug as search-submit before the fix.
      if (Platform.OS !== 'web') {
        if (navigation?.canGoBack?.()) {
          navigation.goBack();
          return;
        }

        if (navigation) {
          navigation.navigateToList(listingParams, { replace: true });
          return;
        }

        if (getRouter().canGoBack?.()) {
          getRouter().back();
          return;
        }

        getRouter().replace({
          pathname: routes?.HOTELS_LIST ?? '/search',
          params: listingParams,
        });
        return;
      }

      if (navigation) {
        navigation.navigateToList(listingParams);
      } else {
        getRouter().push({
          pathname: routes?.HOTELS_LIST ?? '/search',
          params: listingParams,
        });
      }
    } else if (isFromMapView) {
      const mapParams = sanitizeMapNavigationParams(backSearchCriteriaParams);

      if (Platform.OS === 'web') {
        // Prefer the exact map URL saved before leaving (edit/error recovery).
        const returnUrl = consumeMapEditReturnUrl();
        if (returnUrl && typeof window !== 'undefined') {
          window.location.replace(returnUrl);
          return;
        }

        if (navigation) {
          navigation.navigateToMapView(mapParams, { replace: true });
        } else {
          getRouter().replace({
            pathname: routes?.MAP_PAGE ?? '/mapview',
            params: mapParams,
          });
        }
        return;
      }

      if (navigation) {
        navigation.navigateToMapView(
          { ...mapParams, from_mapview: '1' },
          { replace: true },
        );
      } else {
        getRouter().replace({
          pathname: routes?.MAP_PAGE ?? '/mapview',
          params: { ...mapParams, from_mapview: '1' },
        });
      }
    } else if (hotelConfig?.backHandler) {
      hotelConfig.backHandler();
    } else if (backHandler) {
      backHandler();
    } else if (navigation) {
      navigation.goBack();
    } else {
      getRouter().back();
    }
  };

  const handleShowToast = (message: string) => {
    setToast(message);
  };

  const handleDismissToast = () => {
    setToast('');
  };

  const persistAndSendSearchHistory = React.useCallback(
    (searchData: HotelSearchData) => {
      if (
        !searchData.destination ||
        !searchData.checkIn ||
        !searchData.checkOut
      ) {
        return;
      }

      const checkin = format(searchData.checkIn, 'yyyy-MM-dd');
      const checkout = format(searchData.checkOut, 'yyyy-MM-dd');

      addRecentSearch(
        searchData.destination,
        checkin,
        checkout,
        searchData.rooms,
      );

      if (
        Platform.OS !== 'web' &&
        NativeModules?.ReactToNative?.sendSearchHistory
      ) {
        const updatedRecentSearches = queryClient.getQueryData<
          HotelRecentSearch[]
        >(RECENT_SEARCHES_QUERY_KEY);
        const searchHistoryEntries = flattenHotelRecentSearchesForHistory(
          updatedRecentSearches,
        );

        if (searchHistoryEntries.length) {
          NativeModules.ReactToNative.sendSearchHistory(
            'hotel',
            searchHistoryEntries,
          );
        }
      }
    },
    [addRecentSearch, queryClient],
  );

  const handleSearchSubmit = React.useCallback(
    (searchData: HotelSearchData) => {
      clearStoredSearchParams();
      clearMapViewNavigationOrigin();
      onNavigate?.();

      // Live URL only — never sessionStorage / resolved criteria leftovers.
      const isFromMapView =
        getStringParamValue(routeParams.from_mapview) === '1' ||
        getStringParamValue(currentWindowUrlParams.from_mapview) === '1';

      // 1. Intercept Map View Search Redirect
      if (
        isFromMapView &&
        searchData.destination &&
        searchData.checkIn &&
        searchData.checkOut
      ) {
        persistAndSendSearchHistory(searchData);

        const checkIn = format(searchData.checkIn, 'yyyy-MM-dd');
        const checkOut = format(searchData.checkOut, 'yyyy-MM-dd');
        const occupancyStr = formatOccupancy(searchData.rooms);
        const adults = searchData.rooms
          .map((room) => String(room.adults))
          .join(',');
        const children = searchData.rooms
          .flatMap((room, roomIndex) =>
            room.children.map((child) => `${roomIndex + 1}_${child.age}`),
          )
          .join(',');
        const days = String(
          Math.max(
            differenceInCalendarDays(searchData.checkOut, searchData.checkIn),
            1,
          ),
        );

        const selectedHotelId =
          searchData.destination.type?.toLowerCase() === HOTEL
            ? searchData.destination.aa_id?.trim() || ''
            : '';
        const isSameHotelDestination =
          searchData.destination.type?.toLowerCase() === HOTEL &&
          Boolean(selectedHotelId) &&
          selectedHotelId === detailOriginParams?.hotelId;

        // Preserve details context ONLY if they are searching for the same hotel.
        // Otherwise strip stale selected-hotel keys carried over from list/map navigation.
        const baseParams = isSameHotelDestination
          ? resolvedSearchCriteriaParams
          : omitSelectedHotelParams(sanitizedSearchCriteriaParams);

        const mapParams = {
          ...baseParams,
          location_id: searchData.destination.id,
          destination_name: searchData.destination.name,
          checkin: checkIn,
          checkout: checkOut,
          occupancy: occupancyStr,
          rooms: String(searchData.rooms.length),
          adults,
          children,
          days,
          currency: parsedSearchCriteriaParams.currency || currency || 'MYR',
          country_code:
            searchData.destination.country_code ||
            parsedSearchCriteriaParams.countryCode ||
            parsedSearchCriteriaParams.country_code ||
            'MY',
          languageId:
            parsedSearchCriteriaParams.languageId ||
            appConfig.locale ||
            'en-gb',
          ...(selectedHotelId
            ? {
                aaId: selectedHotelId,
                user_selected_hotel_id: selectedHotelId,
                user_selected_id: selectedHotelId,
              }
            : {}),
        };

        // Remove cache-busters, map triggers, and stale duplicate keys for a clean URL
        delete (mapParams as Record<string, unknown>).from_mapview;
        delete (mapParams as Record<string, unknown>).v;
        delete (mapParams as Record<string, unknown>).countryCode;

        // Native map edit opens search with replace (map → search), so submit
        // must replace search → map. Popping first lands on list, then replace
        // turns that into map and drops the listing from the stack.
        //
        // Web map edit pushes search on top of map, so pop first then replace
        // the map entry — history stays [list, map], not [list, mapOld, mapNew].
        const canGoBackInApp = navigation?.canGoBack
          ? navigation.canGoBack()
          : typeof getRouter()?.canGoBack === 'function'
            ? getRouter().canGoBack()
            : false;
        const shouldPopBeforeReplaceMap =
          Platform.OS === 'web' && canGoBackInApp;

        if (navigation) {
          if (shouldPopBeforeReplaceMap) {
            navigation.goBack();
            queueMicrotask(() => {
              navigation.navigateToMapView(mapParams, { replace: true });
            });
          } else {
            navigation.navigateToMapView(mapParams, { replace: true });
          }
        } else if (shouldPopBeforeReplaceMap) {
          getRouter().back();
          queueMicrotask(() => {
            getRouter().replace({
              pathname: routes?.MAP_PAGE ?? '/mapview',
              params: mapParams,
            });
          });
        } else {
          getRouter().replace({
            pathname: routes?.MAP_PAGE ?? '/mapview',
            params: mapParams,
          });
        }
        return;
      }

      // 2. Normal Details Origin Logic (if not from Map View)
      if (!detailOriginParams?.hotelId) {
        handleHotelSearch(searchData);
        return;
      }

      const destination = searchData.destination;

      if (!destination || !searchData.checkIn || !searchData.checkOut) {
        return;
      }

      const submittedHotelId = destination.aa_id || '';
      const submittedLocationId = destination.id?.trim() || '';
      const originLocationId =
        parsedSearchCriteriaParams.location_id?.trim() || '';
      const hasLocationChanged =
        Boolean(submittedLocationId) &&
        Boolean(originLocationId) &&
        submittedLocationId !== originLocationId;
      const nextHotelId = submittedHotelId || detailOriginParams.hotelId;
      const isSameHotelDestination =
        destination.type === 'hotel' &&
        Boolean(submittedHotelId) &&
        submittedHotelId === detailOriginParams.hotelId &&
        !hasLocationChanged;

      if (!isSameHotelDestination) {
        const isNewHotelSelection =
          destination.type?.toLowerCase() === HOTEL &&
          Boolean(submittedHotelId);
        handleHotelSearch({
          ...searchData,
          destinationChanged: true,
          destination: {
            ...destination,
            aa_id: isNewHotelSelection ? submittedHotelId : '',
            city_id: destination.city_id ?? null,
          },
        });
        return;
      }

      const nextLocationId =
        submittedLocationId || parsedSearchCriteriaParams.location_id || '';

      if (!nextHotelId || !nextLocationId) {
        return;
      }

      persistAndSendSearchHistory(searchData);

      const nextDetailsUrl = buildHotelDetailsUrl({
        currentSearchParams: sanitizedSearchCriteriaParams,
        hotelId: nextHotelId,
        destination: {
          ...destination,
          aa_id: nextHotelId,
          city_id: destination.city_id ?? null,
          id: nextLocationId,
        },
        checkIn: searchData.checkIn,
        checkOut: searchData.checkOut,
        rooms: searchData.rooms,
        currency: parsedSearchCriteriaParams.currency || currency || '',
        countryCode:
          destination.country_code ||
          parsedSearchCriteriaParams.countryCode ||
          parsedSearchCriteriaParams.country_code ||
          '',
        languageId:
          parsedSearchCriteriaParams.languageId || appConfig.locale || 'en-gb',
        routePath: detailOriginParams.path,
      });

      if (navigation) {
        navigation.dismissTo(nextDetailsUrl);
      } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(nextDetailsUrl);
      } else {
        getRouter().dismissTo(nextDetailsUrl);
      }
    },
    [
      appConfig.locale,
      currency,
      currentWindowUrlParams,
      detailOriginParams,
      handleHotelSearch,
      onNavigate,
      persistAndSendSearchHistory,
      parsedSearchCriteriaParams.countryCode,
      parsedSearchCriteriaParams.country_code,
      parsedSearchCriteriaParams.currency,
      parsedSearchCriteriaParams.languageId,
      parsedSearchCriteriaParams.location_id,
      resolvedSearchCriteriaParams,
      routeParams.from_mapview,
      currentWindowUrlParams.from_mapview,
      navigation,
      sanitizedSearchCriteriaParams,
    ],
  );

  const isFeatureDestEnabled = useMemo(
    () => enable_hotel_feature_destination === true,
    [enable_hotel_feature_destination],
  );
  const snackbarWidth = isMobile
    ? Math.max(screenWidth - pd.ref.space.spacing40.px * 2, 0)
    : 420;
  const snackbarBottomSpacing =
    safeAreaInsets.bottom + pd.ref.space.spacing40.px;

  return (
    <SafeAreaView style={styles.safeAreaViewContainer} edges={safeAreaEdges}>
      {isMobile && (
        <HeaderView
          titleStart={t('searchHotelPage.headerTitle')}
          onHeaderBack={handleBackToHome}
          startIcon={<ArrowBackIos />}
          supportingText={''}
        />
      )}
      <View style={styles.mainContainer}>
        <ScrollView
          style={{
            flex: 1,
            backgroundColor: getColor(pd.sys.colour.container_none),
          }}
        >
          {enable_hotel_search_banner === true && (
            <View style={styles.bannerSection}>
              <HotelBannerView countryCode={countryCode} />
            </View>
          )}
          <View style={styles.container}>
            <SearchFormView
              initialValues={initialFormValues}
              prefillData={detailSearchPrefillData}
              onError={handleShowToast}
              onSubmit={handleSearchSubmit}
            />
            {isMobile && hotel_search_voucher_enabled === true && (
              <VoucherCarouselWidget onToast={handleShowToast} />
            )}
            {isMobile && hotel_recent_searches_enabled === true && (
              <View>
                <RecentSearchView onNavigate={onNavigate} />
              </View>
            )}
            {isFeatureDestEnabled && isMobile && (
              <View style={styles.featureDestView}>
                <FeatureDestinationView onNavigate={onNavigate} />
              </View>
            )}
          </View>
          {hotel_recommendation_enabled === true && (
            <View style={styles.hotelDealsContainer}>
              <HotelDealsCarouselView />
            </View>
          )}
        </ScrollView>
        {toastMessage ? (
          <View pointerEvents="box-none" style={styles.toastContainer}>
            <SnackbarView
              testID="voucher-snackbar"
              message={toastMessage}
              duration={SnackbarDuration.SHORT}
              bottomSpacing={snackbarBottomSpacing}
              snackbarWidth={snackbarWidth}
              onDismiss={handleDismissToast}
            />
          </View>
        ) : null}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create((pd) => ({
  bannerSection: {
    position: 'relative',
    display: { xs: 'flex', md: 'none' },
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },

  safeAreaViewContainer: {
    flex: 1,
  },
  mainContainer: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.container_none),
  },
  container: {
    flex: 1,
    zIndex: 1,
  },
  text: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  featureDestView: {
    paddingTop: pd.ref.space.spacing30.px,
    display: { xs: 'flex', md: 'none' },
  },
  hotelDealsContainer: {
    display: { xs: 'flex', md: 'none' },
  },
  toastContainer: {
    bottom: pd.ref.space.spacing40.px,
    left: pd.ref.space.spacing40.px,
    right: pd.ref.space.spacing40.px,
    pointerEvents: 'box-none',
    zIndex: 1000,
    position: Platform.select({
      web: 'fixed' as any,
      default: 'absolute',
    }),
  },
}));

export default HotelSearchView;
